import { useMemo } from 'react';
import { useAbility } from '../auth/abilityContext';
import { useUserDirectory } from './userDirectory';
import { useActiveUsers } from './useActiveUsers';
import type { EmploymentWindow } from './employment';

import { compareNatural } from '../../utils/compare';

/**
 * Who a person field offers, and whether it may be written past that list.
 *
 * The list is the app's ACTIVE accounts and nothing else — and, given a
 * window, only those employed during it (see ./employment). A name that matches
 * one gets the account LINK on save (`usePersonUserId`), and that link is the
 * identity every later comparison runs on. Every name typed beside an account
 * is a second spelling of somebody, and there is no way back from one.
 *
 * What is ALREADY on a record stays valid and stays selectable — a day from
 * last year names somebody who has since left. That is the widget's business,
 * not this list's: every person picker folds its own current value in (see
 * PersonPicker).
 *
 * `freeText` is the deliberate exception: office roles that hire subcontractors
 * and take on students need to name people the app has no account for — see the
 * `nameFreely` action in shared/src/auth/ability.ts.
 */
export interface PersonFieldOptions {
    /** Display names of the active accounts, sorted. */
    options: string[];
    /** May this user commit a name that is not on the list? */
    freeText: boolean;
    /**
     * Of those, the ones marked as possible Grabungsleiter. The three leader
     * fields offer them as a group of their own, ahead of everyone else — a dig
     * is led by a handful of people out of fifty.
     *
     * Names, not ids, because that is what a person field stores and compares.
     */
    excavationLeads: ReadonlySet<string>;
    /**
     * Whether the names are the REAL ones yet.
     *
     * A display name comes off the user profile, and until the profiles have
     * been read `useUserLabelState` answers with the Synology login name — it
     * cannot tell "no custom name" from "not loaded". A picker built in that
     * window would offer `P.Eichelbroenner` AS A VALID PICK, and what is picked
     * is stored as text and stays wrong: that is how 46 Tagebuecher came to be
     * filed under the login name. So the options stay EMPTY until this is true.
     */
    ready: boolean;
}

export function usePersonNameOptions(
    /** The record's day or span; none offers every active account. */
    window?: EmploymentWindow
): PersonFieldOptions {
    const { label, profilesReady: ready, profiles } = useUserDirectory();
    const users = useActiveUsers(window);
    const freeText = useAbility().can('nameFreely', 'Person');
    const options = useMemo(
        () =>
            ready
                ? [...new Set(users.map(label).filter(Boolean))].sort(
                      (firstLabel, secondLabel) =>
                          compareNatural(firstLabel, secondLabel)
                  )
                : [],
        [users, label, ready]
    );
    const excavationLeads = useMemo(() => {
        if (!ready) {
            return new Set<string>() as ReadonlySet<string>;
        }
        const marked = new Set(
            profiles
                .filter((profile) => profile.canLeadExcavation)
                .map((profile) => profile.id)
        );
        return new Set(
            users.filter((user) => marked.has(user.id)).map(label)
        ) as ReadonlySet<string>;
    }, [profiles, users, label, ready]);
    return useMemo(
        () => ({ options, freeText, excavationLeads, ready }),
        [options, freeText, excavationLeads, ready]
    );
}
